import * as React from 'react';

import { Analytics } from '~/libs/analytics';
import { CompassThemeProvider } from '@circleci/compass';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NextPageContext } from 'next';
import { AppProps } from 'next/app';
import Head from 'next/head';
// Register the exception logger to catch all uncaught exceptions
import { useRouter } from 'next/router';
import { CookiesProvider, Cookies } from 'react-cookie';

import { AppProvider } from '~/contexts/AppContext';
import { MeProvider } from '~/contexts/MeContext';
import { I18nProvider } from '~/i18n';
import { Language } from '~/i18n/messages';
import { isBrowser } from '~/utils/isBrowser';

const AnalyticsWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  return isBrowser() ? (
    <Analytics isOuterPage={true}>{children}</Analytics>
  ) : (
    <>{children}</>
  );
};

interface Props extends AppProps {
  ctx: NextPageContext;
  cookies: Cookies;
}

const MyApp = ({ Component, pageProps, cookies }: Props): JSX.Element => {
  const router = useRouter();
  const [shortLocale] = router.locale ? router.locale.split('-') : ['en'];

  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: false } },
  });

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <AnalyticsWrapper>
          <CookiesProvider cookies={isBrowser() ? undefined : cookies}>
            <AppProvider>
              <MeProvider>
                <CompassThemeProvider>
                  <Head>
                    <title>CircleCI</title>
                    <meta
                      name="viewport"
                      content="width=device-width, initial-scale=1"
                    />
                  </Head>
                  <I18nProvider locale={shortLocale as Language}>
                    <Component {...pageProps} router={router} />{' '}
                    {/* Let's not pass the router anymore in the future */}
                  </I18nProvider>
                </CompassThemeProvider>
              </MeProvider>
            </AppProvider>
          </CookiesProvider>
        </AnalyticsWrapper>
      </QueryClientProvider>
    </>
  );
};

const getCookies = (ctx) => {
  if (ctx && ctx.req && ctx.req.headers.cookie) {
    return new Cookies(ctx.req.headers.cookie);
  }
  return new Cookies();
};

/* IMPORTANT: getInitialProps is required for Server Side Rendering in nextjs. */
MyApp.getInitialProps = async ({ Component, ctx }: Props): Promise<any> => {
  let pageProps = {};

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  const cookies = getCookies(ctx);
  return { pageProps, cookies };
};

export default MyApp;
